import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  HomeIcon,
  IdentificationIcon,
  UserCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../core/providers/AuthProvider';
import { ROLES } from '../constant/roles';
import { confirmAction } from '../utils/dialogs';
import NotificationBell from './NotificationBell';

// Quick-access nav for the Quick Links cards each role reaches most often. Home and Students
// have no role restriction so every staff role sees them. Home uses `end` so it isn't also
// marked active on every other /dashboard/* route.
const TEACHER_NAV_LINKS = [
  { label: 'Home', to: '/dashboard', icon: HomeIcon, end: true },
  { label: 'Students', to: '/dashboard/students', icon: IdentificationIcon },
  { label: 'Grades', to: '/dashboard/grades', icon: AcademicCapIcon },
  { label: 'Attendance', to: '/dashboard/attendance-summary', icon: CalendarDaysIcon },
  { label: 'Advisory', to: '/dashboard/advisory', icon: UserGroupIcon },
];

const ADMIN_NAV_LINKS = [
  { label: 'Home', to: '/dashboard', icon: HomeIcon, end: true },
  { label: 'Students', to: '/dashboard/students', icon: IdentificationIcon },
  { label: 'Subjects', to: '/dashboard/subjects', icon: BookOpenIcon },
  { label: 'Attendance', to: '/dashboard/attendance-summary', icon: CalendarDaysIcon },
  { label: 'Teachers', to: '/dashboard/teachers', icon: UserGroupIcon },
];

const NAV_LINKS_BY_ROLE = {
  [ROLES.TEACHER]: TEACHER_NAV_LINKS,
  [ROLES.ADMIN]: ADMIN_NAV_LINKS,
  [ROLES.SUPER_ADMIN]: ADMIN_NAV_LINKS,
};

function NavLinkItems({ links }) {
  return links.map(({ label, to, icon: Icon, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-1.5 shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition ${
          isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  ));
}

function AdminHeader() {
  const { role, signOut, mustChangeLogin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  // Hidden until a teacher has chosen their own login, since RoleGuard would only send them back.
  const navLinks = mustChangeLogin ? [] : NAV_LINKS_BY_ROLE[role] ?? [];
  const isTeacher = role === ROLES.TEACHER;
  // Super admins approve the students teachers add; teachers are told which of theirs still wait.
  const showStudentAlerts = role === ROLES.SUPER_ADMIN || role === ROLES.TEACHER;

  const handleSignOut = async () => {
    const confirmed = await confirmAction({
      title: 'Are you sure you want to log out?',
      text: "You'll need to sign in again to continue.",
      confirmText: 'Sign Out',
      danger: true,
    });
    if (confirmed) signOut();
  };

  return (
    <header className="w-full shrink-0 bg-[#0b4a71] border-b border-gray-200 pl-4 pr-2 py-4">
      <div className="flex items-center justify-between gap-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/images/pawigrade.png" alt="Logo" className="h-12 w-auto" />
          <p className="text-2xl font-bold">
            <span className="text-[#4ea6d0]">Pawi</span>
            <span className="text-white">Grade</span>
          </p>
        </Link>

        <div className="flex items-center gap-4">
          {navLinks.length > 0 && (
            <nav className="hidden md:flex items-center gap-1 overflow-x-auto">
              <NavLinkItems links={navLinks} />
            </nav>
          )}

          <div className="flex items-center gap-3">
            {showStudentAlerts && <NotificationBell />}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open account menu"
              className="pr-2 text-white/80 hover:text-white"
            >
              <UserCircleIcon className="h-9 w-9" />
            </button>
          </div>
        </div>
      </div>

      {navLinks.length > 0 && (
        <nav className="md:hidden mt-3 -mx-1 flex items-center gap-1 overflow-x-auto px-1">
          <NavLinkItems links={navLinks} />
        </nav>
      )}

      <div
        className={`fixed inset-0 z-40 ${menuOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-in-out ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMenuOpen(false)}
        />
        <div
          className={`absolute inset-y-0 right-0 flex w-64 flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
            <span className="text-lg font-semibold text-[#0b4a71]">Account</span>
            <button type="button" onClick={() => setMenuOpen(false)} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {isTeacher && (
            <Link
              to="/dashboard/profile"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <UserCircleIcon className="h-5 w-5" />
              Profile
            </Link>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Sign Out
          </button>
        </div>
      </div>
    </header>
  );
}

export default AdminHeader;
